import { useQueryClient } from '@tanstack/react-query'

import { usePasarAFev } from '@/modules/bodega/usePasarAFev'
import {
  useCambiarDocumentoAlAlistar,
  type DocumentoAlAlistar as Decision,
} from './useDocumentoAlAlistar'
import estilos from './DocumentoAlAlistar.module.css'

/**
 * FEV O REMISIÓN, DESDE LA COTIZACIÓN (0296).
 *
 * Johan: «un botón desde la cotización para saber si una COT se debe volver
 * FEV o REMISIÓN al momento de pasar a alistamiento a bodega, y cuando allá
 * alisten que se vuelva REM o FEV según lo decidido».
 *
 * Dos opciones, la elegida marcada. Se cambia hasta que bodega la pasa;
 * desde ahí queda fija y lo dice.
 */
const OPCIONES: { valor: 'fev' | 'remision'; texto: string; ayuda: string }[] = [
  { valor: 'fev', texto: 'FEV', ayuda: 'Factura electrónica' },
  { valor: 'remision', texto: 'Remisión', ayuda: 'Sale el material, se factura después' },
]

export function DocumentoAlAlistar({
  ordenServicioId,
  decision,
  puedeCambiar,
}: {
  ordenServicioId: string
  decision: Decision
  puedeCambiar: boolean
}) {
  const cambiar = useCambiarDocumentoAlAlistar(ordenServicioId)
  const pasar = usePasarAFev()
  const cliente = useQueryClient()
  const fija = decision.pasada || !puedeCambiar
  // 0301: solo servicios no pasa por bodega.
  const servicios = decision.solo_servicios && !decision.pasada

  return (
    <div className={estilos.caja}>
      <p className={estilos.rotulo}>Al alistarla en bodega se vuelve</p>
      <div className={estilos.opciones} role="radiogroup" aria-label="Documento al alistar">
        {OPCIONES.map((o) => {
          const elegida = decision.documento === o.valor
          // 0299: sin IVA World Office no la factura.
          const cerrada = o.valor === 'fev' && decision.sin_iva
          return (
            <button
              key={o.valor}
              type="button"
              role="radio"
              aria-checked={elegida}
              className={`${estilos.opcion} ${elegida ? estilos.elegida : ''}`}
              disabled={fija || cerrada || cambiar.isPending}
              onClick={() =>
                !elegida && cambiar.mutate({ cotizacionId: decision.id, documento: o.valor })
              }
            >
              <span className={estilos.opcionTexto}>{o.texto}</span>
              <span className={estilos.opcionAyuda}>{o.ayuda}</span>
            </button>
          )
        })}
      </div>
      {!decision.pasada && decision.sin_iva && (
        <p className={estilos.nota}>No lleva IVA: World Office no la puede facturar, va a remisión.</p>
      )}
      {!decision.pasada && !puedeCambiar && (
        <p className={estilos.nota}>Lo decide quien hace la cotización.</p>
      )}
      {decision.pasada && (
        <p className={estilos.nota}>
          Bodega ya la pasó a {decision.documento === 'remision' ? 'remisión' : 'FEV'}: no se
          cambia.
        </p>
      )}
      {servicios && decision.documento === 'remision' && (
        <p className={estilos.nota}>
          Son servicios: no pasan por bodega. Se vuelve remisión sola cuando la orden de trabajo
          entre en ejecución.
        </p>
      )}
      {servicios && decision.documento === 'fev' && (
        <div className={estilos.servicios}>
          <p className={estilos.nota}>
            Son servicios: no pasan por bodega. Se pasa a FEV desde aquí o desde cartera
            {decision.aprobada ? '.' : ', cuando el cliente la acepte.'}
          </p>
          {decision.aprobada && puedeCambiar && (
            <button
              type="button"
              className={estilos.pasar}
              disabled={pasar.isPending}
              onClick={() =>
                pasar.mutate(decision.id, {
                  onSuccess: () =>
                    void cliente.invalidateQueries({
                      queryKey: ['documento-al-alistar', ordenServicioId],
                    }),
                })
              }
            >
              {pasar.isPending ? 'Pasando…' : 'Pasar a FEV'}
            </button>
          )}
          {pasar.error && <p className={estilos.error}>{pasar.error.message}</p>}
        </div>
      )}
      {!decision.pasada && !decision.solo_servicios && decision.documento === 'remision' && (
        <p className={estilos.nota}>
          La remisión desde la app todavía no está encendida en World Office: bodega la deja lista y
          queda en espera.
        </p>
      )}
      {cambiar.error && <p className={estilos.error}>{cambiar.error.message}</p>}
    </div>
  )
}
